import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import useRequiredFields from "../../../../hooks/useRequiredFields";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Swal from "sweetalert2";

const CreateOvertime = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({ rate: "0", total_hours: "0" });
  const [staff, setStaff] = useState([]);
  const [overtimeData, setOvertimeData] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [error, setError] = useState({});
  const { checkRequiredFields } = useRequiredFields();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        const res = await Http.get("/staff2");
        setStaff(res.data);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    getCurrentDateInput();
  }, []);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // Convert time format "H:MM" or "HH:MM" to decimal hours
  const convertTimeToDecimal = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return parseFloat((hours + minutes / 60).toFixed(2));
  };

  // Handle changes in overtime data table
  const handleOvertimeDataChange = (index, field, value) => {
    const updatedData = [...overtimeData];
    updatedData[index] = { ...updatedData[index], [field]: value };

    // Calculate total amount when OT hours changed
    if (field === "ot_hours") {
      const hourlyRate = parseFloat(updatedData[index].hourly_rate || 0);
      const otHours = parseFloat(value || 0);
      updatedData[index].total_amount = (hourlyRate * otHours).toFixed(2);
    }

    setOvertimeData(updatedData);
  };

  const selectHandleChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // Replace the generateTuesdays function with this:
  // Replace the generateTuesdays function with this:
  // Replace the generateTuesdays function with this:
  // Replace the generateTuesdays function with this debug version:
  const generateTuesdays = () => {
    const tuesdays = [];
    const today = new Date();

    // console.log("Today:", today.toDateString(), "Day of week:", today.getDay());

    // Find the most recent Tuesday (could be today or in the past)
    const mostRecentTuesday = new Date(today);
    const daysFromTuesday = (today.getDay() + 7 - 2) % 7;
    mostRecentTuesday.setDate(today.getDate() - daysFromTuesday);

    // console.log("Most recent Tuesday:", mostRecentTuesday.toDateString());

    // Generate 21 Tuesdays: 10 before, current, 10 after
    for (let i = -15; i <= 1; i++) {
      const tuesday = new Date(mostRecentTuesday);
      tuesday.setDate(mostRecentTuesday.getDate() + i * 7);
      tuesdays.push(tuesday);

      // if (i === 0) {
      //   console.log("Current Tuesday (i=0):", tuesday.toDateString());
      //   console.log("Is today?", tuesday.toDateString() === today.toDateString());
      // }
    }

    // console.log("All Tuesdays:", tuesdays.map(d => d.toDateString()));

    return tuesdays;
  };

  const formatDate = (date) => {
    const options = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Replace the showTuesdayDatePicker function with this:
  const showTuesdayDatePicker = () => {
    const tuesdays = generateTuesdays();
    const todayString = new Date().toDateString(); // Create this once outside the loop

    // Create HTML for date grid (5 dates per row)
    let dateGridHTML = '<div style="max-height: 400px; overflow-y: auto;">';
    for (let i = 0; i < tuesdays.length; i += 5) {
      dateGridHTML +=
        '<div style="display: flex; gap: 8px; margin-bottom: 8px; justify-content: center;">';

      for (let j = i; j < Math.min(i + 5, tuesdays.length); j++) {
        const date = tuesdays[j];
        const formattedDate = formatDate(date);
        const dateValue = formatDateForInput(date);
        const isToday = date.toDateString() === todayString; // Use the pre-created todayString

        console.log(`Date: ${date.toDateString()}, IsToday: ${isToday}`); // Debug log

        dateGridHTML += `
        <button 
          type="button" 
          class="swal2-confirm swal2-styled date-btn" 
          data-date="${dateValue}"
          style="
            background-color: ${isToday ? "#28a745" : "#007bff"}; 
            color: white; 
            border: none; 
            padding: 8px 18px; 
            border-radius: 4px; 
            font-size: 12px; 
            cursor: pointer;
            min-width: 80px;
            text-align: center;
          "
          onmouseover="this.style.opacity='0.8'"
          onmouseout="this.style.opacity='1'"
        >
          ${formattedDate}
        </button>
      `;
      }

      dateGridHTML += "</div>";
    }
    dateGridHTML += "</div>";

    Swal.fire({
      title: "Select Tuesday Date",
      html: dateGridHTML,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Close",
      width: "800px", // Add this line
      customClass: {
        popup: "tuesday-date-picker",
      },
      didOpen: () => {
        // Add click event listeners to date buttons
        const dateButtons = document.querySelectorAll(".date-btn");
        dateButtons.forEach((button) => {
          button.addEventListener("click", (e) => {
            const selectedDate = e.target.getAttribute("data-date");
            setInputs((values) => ({ ...values, from: selectedDate }));
            Swal.close();
          });
        });
      },
    });
  };

  const getCurrentDateInput = () => {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    const shortDate = `${year}-${month}-${day}`;
    setInputs((values) => ({ ...values, ["date"]: shortDate }));
  };

  const SearchFormSubmit = () => {
    if (inputs.from) {
      setLoading(true);
      Http.get("/get-tuesday-attendance?from=" + inputs.from)
        .then((res) => {
          // Initialize overtime data with the new API response structure
          // const initialOvertimeData = res.data.map((staff) => {
          //   const otHours = convertTimeToDecimal(staff.total_overtime_hours);
          //   const hourlyRate = parseFloat(staff.hourly_rate || 0);
          //   const totalAmount = (hourlyRate * otHours).toFixed(2);

          //   return {
          //     ...staff,
          //     hourly_rate: staff.hourly_rate || "0",
          //     ot_hours: otHours.toString(), // Editable field
          //     total_amount: totalAmount,
          //     remarks: "",
          //   };
          // });
          const initialOvertimeData = res.data.map((staff) => {
            const otHours = parseFloat(staff.total_overtime_hours || 0); // ✅ use decimal directly
            const hourlyRate = parseFloat(staff.hourly_rate || 0);
            const totalAmount = (hourlyRate * otHours).toFixed(2);

            return {
              ...staff,
              hourly_rate: staff.hourly_rate || "0",
              ot_hours: otHours.toString(), // now will show correctly in input
              total_amount: totalAmount,
              remarks: "",
            };
          });
          setOvertimeData(initialOvertimeData);
          setError({}); // clear previous errors
        })
        .catch((error) => {
          Swal.fire({
            title: "Error",
            text: error.response.data?.error || "Failed to fetch data",
            icon: "error",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const formSubmit = () => {
    // Filter entries with overtime hours > 0 AND total_amount > 0
    const overtimeEntries = overtimeData.filter(
      (entry) =>
        parseFloat(entry.ot_hours || 0) > 0 &&
        parseFloat(entry.total_amount || 0) > 0
    );

    if (overtimeEntries.length === 0) {
      alert(
        "Please enter overtime hours for at least one staff member with a valid rate."
      );
      return;
    }

    // Prepare data for batch submission
    const submitData = {
      overtime_entries: overtimeEntries.map((entry) => ({
        hr_staff_basic_info_id: entry.hr_staff_basic_info_id,
        date: entry.tuesday_date, // Use tuesday_date from response
        rate: parseFloat(entry.hourly_rate || 0),
        total_hours: parseFloat(entry.ot_hours || 0),
        total_amount: parseFloat(entry.total_amount || 0),
        remarks: entry.remarks || "",
      })),
    };

    setLoading(true);

    Http.post("/transaction", submitData)
      .then((res) => {
        navigate("/overtime-tuesday", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((error) => {
        console.error("Submit error:", error);
        setError(
          error.response?.data?.errors || {
            general: "Error creating overtime entries",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Create Overtime Form</h5>
        </div>

        {/* Search Section */}
        <div className="am-pagebody">
          <div className="card pd-sm-40">
            <div className="row">
              <div className="col-lg">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    name="from"
                    value={
                      inputs.from
                        ? new Date(inputs.from).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : ""
                    }
                    readOnly
                    placeholder="Select Tuesday Date"
                  />
                  <div className="input-group-append">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={showTuesdayDatePicker}
                    >
                      <i className="fa fa-calendar"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-lg">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={SearchFormSubmit}
                >
                  <i className="fa fa-search mg-r-10"></i> Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Overtime Data Table Section */}
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Create Overtime</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>

            {loading ? (
              <ProgressSpinner />
            ) : overtimeData.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>SL</th>
                      <th>Staff Name</th>
                      <th>Date</th>
                      {/* <th>Total Working Hours</th> */}
                      <th>Overtime</th>
                      <th>OT Rate</th>
                      <th>OT Hours</th>
                      <th>Total Amount</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overtimeData.map((staffMember, index) => (
                      <tr key={staffMember.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{staffMember.staff_name}</strong>
                          <br />
                          <small className="text-muted">
                            {"ID: " + staffMember.staff_card_no || "N/A"}
                          </small>
                          <br />
                          <small className="text-info">
                            {staffMember.shift_name} Shift
                          </small>
                        </td>
                        <td>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={staffMember.tuesday_date || ""}
                            readOnly
                          />
                        </td>
                        {/* <td>
                          <span className="badge badge-info">
                            {staffMember.total_working_hour}
                          </span>
                        </td> */}
                        <td>
                          <span className="badge badge-warning">
                            {staffMember.total_overtime_hours}
                          </span>
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <div className="input-group-prepend">
                              <span className="input-group-text">৳</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              value={staffMember.hourly_rate || ""}
                              readOnly
                              placeholder="Hourly Rate"
                            />
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.25"
                            className="form-control form-control-sm"
                            value={staffMember.ot_hours || ""}
                            onChange={(e) =>
                              handleOvertimeDataChange(
                                index,
                                "ot_hours",
                                e.target.value
                              )
                            }
                            placeholder="OT Hours"
                          />
                        </td>
                        <td>
                          <div className="input-group input-group-sm">
                            <div className="input-group-prepend">
                              <span className="input-group-text">৳</span>
                            </div>
                            <input
                              type="text"
                              className="form-control"
                              value={staffMember.total_amount || "0"}
                              readOnly
                            />
                          </div>
                        </td>
                        <td>
                          <textarea
                            className="form-control form-control-sm"
                            rows="2"
                            value={staffMember.remarks || ""}
                            onChange={(e) =>
                              handleOvertimeDataChange(
                                index,
                                "remarks",
                                e.target.value
                              )
                            }
                            placeholder="Remarks"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="row mt-3">
                  <div className="col-md-4">
                    <button
                      className="btn btn-success btn-lg btn-block"
                      onClick={formSubmit}
                    >
                      <i className="fa fa-save mg-r-10"></i> Create Overtime
                      Entries
                    </button>
                  </div>
                  <div className="col-md-8">
                    <div className="alert alert-info">
                      <i className="fa fa-info-circle"></i>
                      Only staff members with overtime hours greater than 0 will
                      be submitted.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="alert alert-warning">
                <i className="fa fa-exclamation-triangle"></i>
                Please search for a Tuesday date to load staff attendance data.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOvertime;
